import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { 
  HardHat, Search, Loader2, Building2, User, Phone, Mail, Briefcase,
  ChevronLeft, ChevronDown, Receipt, FileSignature, Calendar, ClipboardList, FileText, 
  Package, Eye, Users, MapPin, Filter, RefreshCw, Plus, Save, Upload, File, X, DollarSign, Trash2, CheckCircle
} from 'lucide-react';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => {
  const token = localStorage.getItem('fortis_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const PurchasingSubcontractors = ({ viewMode = 'purchasing' }) => {
  // viewMode: 'purchasing' (default), 'muhasebe', veya 'board'
  // muhasebe modunda hakediş sadece 3 onay tamamlandığında görünür
  // board modunda Yönetim Kurulu onayı verilebilir
  const isMuhasebeMode = viewMode === 'muhasebe';
  const isBoardMode = viewMode === 'board';
  const { user } = useAuth();
  
  const [subcontractors, setSubcontractors] = useState([]);
  const [filteredSubcontractors, setFilteredSubcontractors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBranch, setSelectedBranch] = useState('all');
  const [branches, setBranches] = useState([]);
  
  // Muhasebe modunda expandable row için
  const [expandedSubcontractorId, setExpandedSubcontractorId] = useState(null);
  const [expandedHakedisData, setExpandedHakedisData] = useState([]);
  const [expandedLoading, setExpandedLoading] = useState(false);
  
  // Detail view states
  const [detailView, setDetailView] = useState(false);
  const [selectedSubcontractor, setSelectedSubcontractor] = useState(null);
  const [activeTab, setActiveTab] = useState('hakedis');
  const [tabLoading, setTabLoading] = useState(false);
  
  // Tab data states
  const [hakedisData, setHakedisData] = useState([]);
  const [sozlesmeData, setSozlesmeData] = useState([]);
  const [isProgramiData, setIsProgramiData] = useState([]);
  const [tutanakData, setTutanakData] = useState([]);
  const [isBilgisiData, setIsBilgisiData] = useState([]);
  const [malzemeData, setMalzemeData] = useState([]);
  const [depoMalzemeleri, setDepoMalzemeleri] = useState([]);
  
  // Detail modal
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  // Add new subcontractor modal
  const [showAddModal, setShowAddModal] = useState(false);
  const [addLoading, setAddLoading] = useState(false);
  const [allBranches, setAllBranches] = useState([]);
  const [cariHesaplar, setCariHesaplar] = useState([]);
  const [newSubcontractor, setNewSubcontractor] = useState({
    name: '',
    cari_hesap_id: '',
    branch_id: '',
    company_duties: '',
    contacts: [{ name: '', role: '', phone: '', email: '' }],
    tax_number: '',
    address: ''
  });

  // Sözleşme ekleme modal state
  const [showContractModal, setShowContractModal] = useState(false);
  const [contractLoading, setContractLoading] = useState(false);
  const [newContract, setNewContract] = useState({
    work_type: '',
    contract_date: '',
    start_date: '',
    end_date: '',
    contract_amount: '',
    currency: 'TL',
    description: '',
    documents: []
  });
  const contractFileInputRef = React.useRef(null);

  // Reset contract form
  const resetContractForm = () => {
    setNewContract({
      work_type: '',
      contract_date: '',
      start_date: '',
      end_date: '',
      contract_amount: '',
      currency: 'TL',
      description: '',
      documents: []
    });
  };

  // Contract file upload
  const handleContractFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    const formDataUpload = new FormData();
    files.forEach(file => formDataUpload.append('files', file));

    try {
      const response = await axios.post(`${API}/upload/offers`, formDataUpload, {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'multipart/form-data'
        }
      });
      setNewContract(prev => ({
        ...prev,
        documents: [...prev.documents, ...response.data.files]
      }));
      toast.success('Belge yüklendi');
    } catch (err) {
      console.error('Dosya yüklenirken hata:', err);
      toast.error('Dosya yüklenemedi');
    }
    e.target.value = '';
  };

  // Remove contract document
  const removeContractDocument = (index) => {
    setNewContract(prev => ({
      ...prev,
      documents: prev.documents.filter((_, i) => i !== index)
    }));
  };

  // Save contract
  const handleSaveContract = async () => {
    if (!newContract.work_type.trim()) {
      toast.error('İş türü zorunludur');
      return;
    }
    if (!selectedSubcontractor) {
      toast.error('Taşeron seçilmedi');
      return;
    }

    setContractLoading(true);
    try {
      await axios.post(
        `${API}/purchasing/subcontractors/${selectedSubcontractor.id}/sozlesme`,
        newContract,
        { headers: getAuthHeaders() }
      );
      toast.success('Sözleşme başarıyla eklendi');
      setShowContractModal(false);
      resetContractForm();
      // Sözleşmeleri yeniden yükle
      loadTabData('sozlesme');
    } catch (error) {
      console.error('Sözleşme eklenirken hata:', error);
      toast.error(error.response?.data?.detail || 'Sözleşme eklenemedi');
    }
    setContractLoading(false);
  };

  // Load all branches for dropdown
  const loadAllBranches = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/branches`, { headers: getAuthHeaders() });
      setAllBranches(res.data || []);
    } catch (error) {
      console.error('Şubeler yüklenirken hata:', error);
    }
  }, []);

  // Load cari hesaplar for dropdown
  const loadCariHesaplar = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/global/cari-hesaplar`, { headers: getAuthHeaders() });
      setCariHesaplar(res.data || []);
    } catch (error) {
      console.error('Cari hesaplar yüklenirken hata:', error);
    }
  }, []);

  // Reset new subcontractor form
  const resetNewSubcontractorForm = () => {
    setNewSubcontractor({
      name: '',
      cari_hesap_id: '',
      branch_id: '',
      company_duties: '',
      contact_name: '',
      contact_title: '',
      contact_phone: '',
      contact_email: '',
      tax_number: '',
      address: ''
    });
  };

  // Handle add new subcontractor
  const handleAddSubcontractor = async () => {
    if (!newSubcontractor.name.trim()) {
      toast.error('Taşeron adı zorunludur');
      return;
    }
    if (!newSubcontractor.branch_id) {
      toast.error('Şube seçimi zorunludur');
      return;
    }

    setAddLoading(true);
    try {
      const res = await axios.post(`${API}/purchasing/subcontractors`, newSubcontractor, { headers: getAuthHeaders() });
      toast.success('Taşeron başarıyla eklendi');
      setSubcontractors(prev => [res.data, ...prev]);
      setShowAddModal(false);
      resetNewSubcontractorForm();
      loadSubcontractors(); // Refresh list
    } catch (error) {
      console.error('Taşeron eklenirken hata:', error);
      toast.error(error.response?.data?.detail || 'Taşeron eklenemedi');
    }
    setAddLoading(false);
  };

  // Load subcontractors
  const loadSubcontractors = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/purchasing/subcontractors`, { headers: getAuthHeaders() });
      setSubcontractors(res.data || []);
      setFilteredSubcontractors(res.data || []);
      
      // Extract unique branches
      const uniqueBranches = [...new Set(res.data.map(s => s.branch_name).filter(Boolean))];
      setBranches(uniqueBranches);
    } catch (error) {
      console.error('Taşeronlar yüklenirken hata:', error);
      toast.error('Taşeronlar yüklenemedi');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    loadSubcontractors();
    loadAllBranches();
    loadCariHesaplar();
  }, [loadSubcontractors, loadAllBranches, loadCariHesaplar]);

  // Filter subcontractors
  useEffect(() => {
    let filtered = subcontractors;
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(s => 
        (s.name || s.company_name || '').toLowerCase().includes(term) ||
        (s.company_duties || '').toLowerCase().includes(term) ||
        (s.branch_name || '').toLowerCase().includes(term)
      );
    }
    
    if (selectedBranch !== 'all') {
      filtered = filtered.filter(s => s.branch_name === selectedBranch);
    }
    
    setFilteredSubcontractors(filtered);
  }, [searchTerm, selectedBranch, subcontractors]);

  // Load tab data
  const loadTabData = useCallback(async (tab) => {
    if (!selectedSubcontractor) return;
    setTabLoading(true);
    try {
      const endpoints = {
        'hakedis': `/purchasing/subcontractors/${selectedSubcontractor.id}/hakedis`,
        'sozlesme': `/purchasing/subcontractors/${selectedSubcontractor.id}/sozlesme`,
        'is-programi': `/purchasing/subcontractors/${selectedSubcontractor.id}/is-programi`,
        'tutanaklar': `/purchasing/subcontractors/${selectedSubcontractor.id}/tutanaklar`,
        'is-bilgisi': `/purchasing/subcontractors/${selectedSubcontractor.id}/is-bilgisi`,
        'malzeme-kullanimi': `/purchasing/subcontractors/${selectedSubcontractor.id}/malzeme-kullanimi`
      };
      
      if (endpoints[tab]) {
        const res = await axios.get(`${API}${endpoints[tab]}`, { headers: getAuthHeaders() });
        switch(tab) {
          case 'hakedis': setHakedisData(res.data || []); break;
          case 'sozlesme': setSozlesmeData(res.data || []); break;
          case 'is-programi': setIsProgramiData(res.data || []); break;
          case 'tutanaklar': setTutanakData(res.data || []); break;
          case 'is-bilgisi': setIsBilgisiData(res.data || []); break;
          case 'malzeme-kullanimi': 
            setMalzemeData(res.data || []);
            // Load depot materials too
            try {
              const depoRes = await axios.get(`${API}/purchasing/subcontractors/${selectedSubcontractor.id}/depo-malzemeleri`, { headers: getAuthHeaders() });
              setDepoMalzemeleri(depoRes.data || []);
            } catch {
              setDepoMalzemeleri([]);
            }
            break;
          default: break;
        }
      }
    } catch (error) {
      console.error(`${tab} verileri yüklenirken hata:`, error);
    }
    setTabLoading(false);
  }, [selectedSubcontractor]);

  useEffect(() => {
    if (selectedSubcontractor && detailView) {
      loadTabData(activeTab);
    }
  }, [selectedSubcontractor, activeTab, detailView, loadTabData]);

  const handleFinishSubcontractorTask = async (id) => {
    if (!window.confirm("Bu taşeronun görevini bitirdiğini İK'ya bildirmek istiyor musunuz?")) return;
    try {
      await axios.post(`${API}/subcontractors/${id}/finish-task`, {}, { headers: getAuthHeaders() });
      toast.success("Görev bitirme bildirimi İK'ya gönderildi");
      loadSubcontractors();
    } catch (error) {
      toast.error('Bildirim gönderilemedi');
    }
  };

  const openSubcontractorDetail = (sub) => {
    setSelectedSubcontractor(sub);
    setDetailView(true);
    setActiveTab('hakedis');
  };

  // Muhasebe modunda satır genişletme (accordion style)
  const toggleExpandSubcontractor = async (sub) => {
    // Aynı satıra tıklanırsa kapat
    if (expandedSubcontractorId === sub.id) {
      setExpandedSubcontractorId(null);
      setExpandedHakedisData([]);
      return;
    }
    
    // Yeni satır aç
    setExpandedSubcontractorId(sub.id);
    setExpandedLoading(true);
    try {
      const res = await axios.get(`${API}/purchasing/subcontractors/${sub.id}/hakedis`, { headers: getAuthHeaders() });
      setExpandedHakedisData(res.data || []);
    } catch (error) {
      console.error('Hakediş yüklenemedi:', error);
      toast.error('Hakediş verileri yüklenemedi');
      setExpandedHakedisData([]);
    }
    setExpandedLoading(false);
  };

  // Muhasebe modunda direkt hakediş göster (aşağı açılır liste şeklinde) - eski fonksiyon
  const openSubcontractorHakedis = async (sub) => {
    setSelectedSubcontractor(sub);
    setTabLoading(true);
    try {
      const res = await axios.get(`${API}/purchasing/subcontractors/${sub.id}/hakedis`, { headers: getAuthHeaders() });
      setHakedisData(res.data || []);
    } catch (error) {
      console.error('Hakediş yüklenemedi:', error);
      toast.error('Hakediş verileri yüklenemedi');
    }
    setTabLoading(false);
    setDetailView(true);
    setActiveTab('hakedis');
  };

  // Yönetim Kurulu onayı
  const handleBoardApproval = async (hakedis) => {
    try {
      const response = await axios.post(
        `${API}/purchasing/subcontractors/${selectedSubcontractor.id}/hakedis/${hakedis.id}/board-approval`,
        {},
        { headers: getAuthHeaders() }
      );
      if (response.data) {
        toast.success('Yönetim Kurulu onayı verildi');
        // Hakediş verilerini yenile
        const res = await axios.get(`${API}/purchasing/subcontractors/${selectedSubcontractor.id}/hakedis`, { headers: getAuthHeaders() });
        setHakedisData(res.data || []);
      }
    } catch (error) {
      console.error('Board approval error:', error);
      toast.error('Onay verilemedi');
    }
  };

  // Satın Alma onayı
  const handlePurchasingApproval = async (hakedis) => {
    try {
      const response = await axios.post(
        `${API}/purchasing/subcontractors/${selectedSubcontractor.id}/hakedis/${hakedis.id}/purchasing-approval`,
        {},
        { headers: getAuthHeaders() }
      );
      if (response.data) {
        toast.success('Satın Alma onayı verildi');
        // Hakediş verilerini yenile
        const res = await axios.get(`${API}/purchasing/subcontractors/${selectedSubcontractor.id}/hakedis`, { headers: getAuthHeaders() });
        setHakedisData(res.data || []);
      }
    } catch (error) {
      console.error('Purchasing approval error:', error);
      toast.error('Onay verilemedi');
    }
  };

  // Onaylanan işlere gönder
  const handleSendToApprovedJobs = async (hakedis, subcontractorId = null) => {
    const subId = subcontractorId || selectedSubcontractor?.id || expandedSubcontractorId;
    if (!subId) {
      toast.error('Taşeron bulunamadı');
      return;
    }
    
    try {
      const response = await axios.post(
        `${API}/purchasing/subcontractors/${subId}/hakedis/${hakedis.id}/send-to-approved`,
        {},
        { headers: getAuthHeaders() }
      );
      if (response.data) {
        toast.success('Hakediş onaylanan işlere gönderildi');
        // Hakediş verilerini yenile
        const res = await axios.get(`${API}/purchasing/subcontractors/${subId}/hakedis`, { headers: getAuthHeaders() });
        if (expandedSubcontractorId === subId) {
          setExpandedHakedisData(res.data || []);
        }
        if (selectedSubcontractor?.id === subId) {
          setHakedisData(res.data || []);
        }
      }
    } catch (error) {
      console.error('Send to jobs error:', error);
      toast.error('İşlere gönderilemedi');
    }
  };

  const openItemDetail = (item) => {
    setSelectedItem(item);
    setDetailModalOpen(true);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('tr-TR');
    } catch {
      return dateStr;
    }
  };

  // Stats
  const totalSubcontractors = subcontractors.length;
  const totalEmployees = subcontractors.reduce((sum, s) => sum + (s.employee_count || 0), 0);
  const branchCount = branches.length;

  // Detail view
  if (detailView && selectedSubcontractor) {
    // Muhasebe modunda sadeleştirilmiş görünüm
    if (isMuhasebeMode) {
      return (
        <div className="space-y-4" data-testid="purchasing-subcontractor-hakedis-view">
          {/* Header */}
          <div className="flex items-center gap-4">
            <Button variant="outline" onClick={() => setDetailView(false)} className="gap-2" data-testid="back-to-list-btn">
              <ChevronLeft className="h-4 w-4" />
              Geri
            </Button>
            <div className="flex-1">
              <h2 className="text-xl font-bold text-gray-900">{selectedSubcontractor.name || selectedSubcontractor.company_name || 'İsimsiz Taşeron'}</h2>
              <p className="text-gray-500 text-sm">{selectedSubcontractor.branch_name}</p>
            </div>
          </div>

          {/* Sadece Hakediş Tablosu */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Receipt className="h-5 w-5 text-orange-600" />
                Onaylı Hakediş Kayıtları (3 Onay Tamamlanmış)
              </CardTitle>
            </CardHeader>
            <CardContent>
              {tabLoading ? (
                <div className="flex justify-center py-8"><Loader2 className="h-8 w-8 animate-spin text-orange-600" /></div>
              ) : (() => {
                const hakedislerToShow = hakedisData.filter(h => h.branch_manager_approved && h.management_approved && h.purchasing_approved);
                
                return hakedislerToShow.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Receipt className="h-12 w-12 mx-auto mb-2 opacity-50" />
                    <p>Onaylı hakediş bulunmuyor (3 onay gerekli)</p>
                  </div>
                ) : (
                  <ScrollArea className="h-[400px]">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Tarih</TableHead>
                          <TableHead>Başlık</TableHead>
                          <TableHead>Miktar</TableHead>
                          <TableHead>Belgeler</TableHead>
                          <TableHead>Onaylar</TableHead>
                          <TableHead>İşlem</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {hakedislerToShow.map(item => (
                          <TableRow key={item.id}>
                            <TableCell>{formatDate(item.hakedis_date)}</TableCell>
                            <TableCell className="font-medium">{item.title}</TableCell>
                            <TableCell className="font-semibold text-green-600">
                              {item.amount ? `${Number(item.amount).toLocaleString('tr-TR')} TL` : '-'}
                            </TableCell>
                            <TableCell><Badge variant="outline">{item.documents?.length || 0} belge</Badge></TableCell>
                            <TableCell>
                              <div className="flex gap-1 flex-wrap">
                                <Badge className="bg-green-100 text-green-700 text-xs">Şube ✓</Badge>
                                <Badge className="bg-blue-100 text-blue-700 text-xs">Yönetim ✓</Badge>
                                <Badge className="bg-purple-100 text-purple-700 text-xs">Satın Alma ✓</Badge>
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="flex gap-1">
                                <Button size="sm" variant="outline" onClick={() => openItemDetail(item)}>
                                  <Eye className="h-4 w-4" />
                                </Button>
                                {!item.sent_to_jobs ? (
                                  <Button 
                                    size="sm" 
                                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                                    onClick={() => handleSendToApprovedJobs(item)}
                                  >
                                    İşlere Gönder
                                  </Button>
                                ) : (
                                  <Badge className="bg-emerald-100 text-emerald-700">İşlere Gönderildi</Badge>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </ScrollArea>
                );
              })()}
            </CardContent>
          </Card>
        </div>
      );
    }
    
    // Normal (purchasing) mod - tam detay görünümü
    return (
      <div className="space-y-6" data-testid="purchasing-subcontractor-detail">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="outline" onClick={() => setDetailView(false)} className="gap-2" data-testid="back-to-list-btn">
            <ChevronLeft className="h-4 w-4" />
            Geri
          </Button>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-gray-900">{selectedSubcontractor.name || selectedSubcontractor.company_name || 'İsimsiz Taşeron'}</h1>
            <div className="flex items-center gap-4 text-gray-500">
              <span className="flex items-center gap-1">
                <MapPin className="h-4 w-4" />
                {selectedSubcontractor.branch_name}
              </span>
              <span className="flex items-center gap-1">
                <Users className="h-4 w-4" />
                {selectedSubcontractor.employee_count || 0} çalışan
              </span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2"><Briefcase className="h-5 w-5 text-orange-600" /> Çalışma Bilgileri</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="flex justify-between py-1 border-b">
                  <span className="text-gray-500">İşe Başlama:</span>
                  <span className="font-medium">{selectedSubcontractor.start_date ? new Date(selectedSubcontractor.start_date).toLocaleDateString('tr-TR') : '-'}</span>
                </div>
                <div className="flex justify-between py-1 border-b">
                  <span className="text-gray-500">Bitiş Tarihi:</span>
                  <span className="font-medium">{selectedSubcontractor.end_date ? new Date(selectedSubcontractor.end_date).toLocaleDateString('tr-TR') : '-'}</span>
                </div>
                <div className="flex justify-between py-1 border-b">
                  <span className="text-gray-500">Verdiği Hizmet:</span>
                  <span className="font-medium">{selectedSubcontractor.provided_service || selectedSubcontractor.company_duties || '-'}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {selectedSubcontractor.custom_fields && selectedSubcontractor.custom_fields.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2"><FileText className="h-5 w-5 text-orange-600" /> Ek Bilgiler</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {selectedSubcontractor.custom_fields.map((field, idx) => (
                    <div key={idx} className="flex justify-between py-1 border-b">
                      <span className="text-gray-500">{field.key}:</span>
                      <span className="font-medium">{field.value}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Contact Info */}
        {selectedSubcontractor.contacts?.length > 0 && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Yetkili Kişiler</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {selectedSubcontractor.contacts.map((contact, idx) => (
                  <div key={idx} className="bg-gray-50 p-3 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <User className="h-4 w-4 text-orange-600" />
                      <span className="font-medium">{contact.name || '-'}</span>
                    </div>
                    {contact.title && <div className="flex items-center gap-2 text-sm text-gray-600"><Briefcase className="h-3 w-3" />{contact.title}</div>}
                    {contact.phone && <div className="flex items-center gap-2 text-sm text-gray-600"><Phone className="h-3 w-3" />{contact.phone}</div>}
                    {contact.email && <div className="flex items-center gap-2 text-sm text-gray-600"><Mail className="h-3 w-3" />{contact.email}</div>}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <div className="overflow-x-auto">
            <TabsList className="inline-flex w-auto min-w-full">
              <TabsTrigger value="hakedis" className="gap-1 px-3">
                <Receipt className="h-4 w-4" /><span className="hidden md:inline">Hakediş</span>
              </TabsTrigger>
              <TabsTrigger value="sozlesme" className="gap-1 px-3">
                <FileSignature className="h-4 w-4" /><span className="hidden md:inline">Sözleşme</span>
              </TabsTrigger>
              <TabsTrigger value="is-programi" className="gap-1 px-3">
                <Calendar className="h-4 w-4" /><span className="hidden md:inline">İş Programı</span>
              </TabsTrigger>
              <TabsTrigger value="tutanaklar" className="gap-1 px-3">
                <ClipboardList className="h-4 w-4" /><span className="hidden md:inline">Tutanaklar</span>
              </TabsTrigger>
              <TabsTrigger value="is-bilgisi" className="gap-1 px-3">
                <FileText className="h-4 w-4" /><span className="hidden md:inline">İş Bilgisi</span>
              </TabsTrigger>
              <TabsTrigger value="malzeme-kullanimi" className="gap-1 px-3">
                <Package className="h-4 w-4" /><span className="hidden md:inline">Malzeme</span>
              </TabsTrigger>
            </TabsList>
          </div>

          {/* Hakediş Tab */}
          <TabsContent value="hakedis" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Receipt className="h-5 w-5 text-orange-600" />
                  {isMuhasebeMode ? 'Onaylı Hakediş Kayıtları' : 'Hakediş Kayıtları'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {tabLoading ? (
                  <div className="flex justify-center py-8"><Loader2 className="h-8 w-8 animate-spin text-orange-600" /></div>
                ) : (() => {
                  // Muhasebe modunda sadece 3 onaylı hakedişleri göster
                  const hakedislerToShow = isMuhasebeMode 
                    ? hakedisData.filter(h => h.branch_manager_approved && h.management_approved && h.purchasing_approved)
                    : hakedisData;
                  
                  return hakedislerToShow.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <Receipt className="h-12 w-12 mx-auto mb-2 opacity-50" />
                      <p>{isMuhasebeMode ? 'Onaylı hakediş bulunmuyor (3 onay gerekli)' : 'Henüz hakediş kaydı yok'}</p>
                    </div>
                  ) : (
                    <ScrollArea className="h-[400px]">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Tarih</TableHead>
                            <TableHead>Başlık</TableHead>
                            <TableHead>Miktar</TableHead>
                            <TableHead>Belgeler</TableHead>
                            <TableHead>Şube Onay</TableHead>
                            <TableHead>Yönetim Onay</TableHead>
                            <TableHead>Satın Alma Onay</TableHead>
                            <TableHead>İşlem</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {hakedislerToShow.map(item => (
                            <TableRow key={item.id}>
                              <TableCell>{formatDate(item.hakedis_date)}</TableCell>
                              <TableCell className="font-medium">{item.title}</TableCell>
                              <TableCell className="font-semibold text-green-600">
                                {item.amount ? `${Number(item.amount).toLocaleString('tr-TR')} TL` : '-'}
                              </TableCell>
                              <TableCell><Badge variant="outline">{item.documents?.length || 0} belge</Badge></TableCell>
                              <TableCell>
                                {item.branch_manager_approved ? 
                                  <Badge className="bg-green-100 text-green-700">✓ Onaylı</Badge> : 
                                  <Badge variant="outline">Bekliyor</Badge>
                                }
                              </TableCell>
                              <TableCell>
                                {item.management_approved ? 
                                  <Badge className="bg-blue-100 text-blue-700">✓ Onaylı</Badge> : 
                                  <Badge variant="outline">Bekliyor</Badge>
                                }
                              </TableCell>
                              <TableCell>
                                {item.purchasing_approved ? 
                                  <Badge className="bg-purple-100 text-purple-700">✓ Onaylı</Badge> : 
                                  <Badge variant="outline">Bekliyor</Badge>
                                }
                              </TableCell>
                              <TableCell>
                                <div className="flex gap-1">
                                  <Button size="sm" variant="outline" onClick={() => openItemDetail(item)}>
                                    <Eye className="h-4 w-4" />
                                  </Button>
                                  {/* Satın Alma Panelinde - Satın Alma onayı */}
                                  {!isMuhasebeMode && !isBoardMode && !item.purchasing_approved && (
                                    <Button 
                                      size="sm" 
                                      className="bg-purple-600 hover:bg-purple-700 text-white"
                                      onClick={() => handlePurchasingApproval(item)}
                                    >
                                      Onayla
                                    </Button>
                                  )}
                                  {/* Yönetim Kurulu Panelinde - Yönetim onayı */}
                                  {isBoardMode && !item.management_approved && (
                                    <Button 
                                      size="sm" 
                                      className="bg-blue-600 hover:bg-blue-700 text-white"
                                      onClick={() => handleBoardApproval(item)}
                                    >
                                      Onayla
                                    </Button>
                                  )}
                                  {/* Muhasebe Panelinde - İşlere Gönder */}
                                  {isMuhasebeMode && item.branch_manager_approved && item.management_approved && item.purchasing_approved && !item.sent_to_jobs && (
                                    <Button 
                                      size="sm" 
                                      className="bg-emerald-600 hover:bg-emerald-700 text-white"
                                      onClick={() => handleSendToApprovedJobs(item)}
                                    >
                                      İşlere Gönder
                                    </Button>
                                  )}
                                  {item.sent_to_jobs && (
                                    <Badge className="bg-emerald-100 text-emerald-700">İşlere Gönderildi</Badge>
                                  )}
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </ScrollArea>
                  );
                })()}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Sözleşme Tab */}
          <TabsContent value="sozlesme" className="mt-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="flex items-center gap-2"><FileSignature className="h-5 w-5 text-orange-600" />Sözleşmeler</CardTitle>
                <Button 
                  onClick={() => setShowContractModal(true)} 
                  className="gap-2 bg-orange-600 hover:bg-orange-700"
                  data-testid="add-contract-btn"
                >
                  <Plus className="h-4 w-4" />
                  Sözleşme Ekle
                </Button>
              </CardHeader>
              <CardContent>
                {tabLoading ? (
                  <div className="flex justify-center py-8"><Loader2 className="h-8 w-8 animate-spin text-orange-600" /></div>
                ) : sozlesmeData.length === 0 ? (
                  <div className="text-center py-8 text-gray-500"><FileSignature className="h-12 w-12 mx-auto mb-2 opacity-50" /><p>Henüz sözleşme yok</p></div>
                ) : (
                  <ScrollArea className="h-[400px]">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Tarih</TableHead>
                          <TableHead>İş Türü</TableHead>
                          <TableHead>Başlangıç</TableHead>
                          <TableHead>Bitiş</TableHead>
                          <TableHead>Tutar</TableHead>
                          <TableHead>Belgeler</TableHead>
                          <TableHead>İşlem</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {sozlesmeData.map(item => (
                          <TableRow key={item.id}>
                            <TableCell>{formatDate(item.contract_date)}</TableCell>
                            <TableCell className="font-medium">{item.work_type}</TableCell>
                            <TableCell>{formatDate(item.start_date)}</TableCell>
                            <TableCell>{formatDate(item.end_date)}</TableCell>
                            <TableCell className="font-semibold text-green-600">
                              {item.contract_amount ? `${Number(item.contract_amount).toLocaleString('tr-TR')} ${item.currency || 'TL'}` : '-'}
                            </TableCell>
                            <TableCell><Badge variant="outline">{item.documents?.length || 0} belge</Badge></TableCell>
                            <TableCell>
                              <Button size="sm" variant="outline" onClick={() => openItemDetail(item)}><Eye className="h-4 w-4" /></Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </ScrollArea>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* İş Programı Tab */}
          <TabsContent value="is-programi" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><Calendar className="h-5 w-5 text-orange-600" />İş Programı</CardTitle>
              </CardHeader>
              <CardContent>
                {tabLoading ? (
                  <div className="flex justify-center py-8"><Loader2 className="h-8 w-8 animate-spin text-orange-600" /></div>
                ) : isProgramiData.length === 0 ? (
                  <div className="text-center py-8 text-gray-500"><Calendar className="h-12 w-12 mx-auto mb-2 opacity-50" /><p>Henüz iş programı yok</p></div>
                ) : (
                  <ScrollArea className="h-[400px]">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Konu</TableHead>
                          <TableHead>Başlangıç</TableHead>
                          <TableHead>Bitiş</TableHead>
                          <TableHead>Belgeler</TableHead>
                          <TableHead>İşlem</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {isProgramiData.map(item => (
                          <TableRow key={item.id}>
                            <TableCell className="font-medium">{item.subject}</TableCell>
                            <TableCell>{formatDate(item.start_date)}</TableCell>
                            <TableCell>{formatDate(item.end_date)}</TableCell>
                            <TableCell><Badge variant="outline">{item.documents?.length || 0} belge</Badge></TableCell>
                            <TableCell>
                              <Button size="sm" variant="outline" onClick={() => openItemDetail(item)}><Eye className="h-4 w-4" /></Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </ScrollArea>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tutanaklar Tab */}
          <TabsContent value="tutanaklar" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><ClipboardList className="h-5 w-5 text-orange-600" />Tutanaklar</CardTitle>
              </CardHeader>
              <CardContent>
                {tabLoading ? (
                  <div className="flex justify-center py-8"><Loader2 className="h-8 w-8 animate-spin text-orange-600" /></div>
                ) : tutanakData.length === 0 ? (
                  <div className="text-center py-8 text-gray-500"><ClipboardList className="h-12 w-12 mx-auto mb-2 opacity-50" /><p>Henüz tutanak yok</p></div>
                ) : (
                  <ScrollArea className="h-[400px]">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Tarih</TableHead>
                          <TableHead>Başlık</TableHead>
                          <TableHead>Belgeler</TableHead>
                          <TableHead>İşlem</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {tutanakData.map(item => (
                          <TableRow key={item.id}>
                            <TableCell>{formatDate(item.report_date)}</TableCell>
                            <TableCell className="font-medium">{item.title}</TableCell>
                            <TableCell><Badge variant="outline">{item.documents?.length || 0} belge</Badge></TableCell>
                            <TableCell>
                              <Button size="sm" variant="outline" onClick={() => openItemDetail(item)}><Eye className="h-4 w-4" /></Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </ScrollArea>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* İş Bilgisi Tab */}
          <TabsContent value="is-bilgisi" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><FileText className="h-5 w-5 text-orange-600" />İş Bilgisi</CardTitle>
              </CardHeader>
              <CardContent>
                {tabLoading ? (
                  <div className="flex justify-center py-8"><Loader2 className="h-8 w-8 animate-spin text-orange-600" /></div>
                ) : isBilgisiData.length === 0 ? (
                  <div className="text-center py-8 text-gray-500"><FileText className="h-12 w-12 mx-auto mb-2 opacity-50" /><p>Henüz iş bilgisi yok</p></div>
                ) : (
                  <ScrollArea className="h-[400px]">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Tarih</TableHead>
                          <TableHead>Başlık</TableHead>
                          <TableHead>Belgeler</TableHead>
                          <TableHead>İşlem</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {isBilgisiData.map(item => (
                          <TableRow key={item.id}>
                            <TableCell>{formatDate(item.report_date)}</TableCell>
                            <TableCell className="font-medium">{item.title}</TableCell>
                            <TableCell><Badge variant="outline">{item.documents?.length || 0} belge</Badge></TableCell>
                            <TableCell>
                              <Button size="sm" variant="outline" onClick={() => openItemDetail(item)}><Eye className="h-4 w-4" /></Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </ScrollArea>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Malzeme Tab */}
          <TabsContent value="malzeme-kullanimi" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><Package className="h-5 w-5 text-orange-600" />Malzeme Kullanımı</CardTitle>
              </CardHeader>
              <CardContent>
                {tabLoading ? (
                  <div className="flex justify-center py-8"><Loader2 className="h-8 w-8 animate-spin text-orange-600" /></div>
                ) : (malzemeData.length === 0 && depoMalzemeleri.length === 0) ? (
                  <div className="text-center py-8 text-gray-500"><Package className="h-12 w-12 mx-auto mb-2 opacity-50" /><p>Henüz malzeme kaydı yok</p></div>
                ) : (
                  <div className="space-y-6">
                    {/* Manuel malzeme kullanımı */}
                    {malzemeData.length > 0 && (
                      <div>
                        <h4 className="font-medium mb-3">Manuel Girilen Malzemeler</h4>
                        <ScrollArea className="h-[200px]">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>Tarih</TableHead>
                                <TableHead>Malzeme</TableHead>
                                <TableHead>Miktar</TableHead>
                                <TableHead>Birim</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {malzemeData.map(item => (
                                <TableRow key={item.id}>
                                  <TableCell>{formatDate(item.usage_date)}</TableCell>
                                  <TableCell className="font-medium">{item.material_name}</TableCell>
                                  <TableCell>{item.quantity}</TableCell>
                                  <TableCell>{item.unit}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </ScrollArea>
                      </div>
                    )}
                    
                    {/* Depodan verilen malzemeler */}
                    {depoMalzemeleri.length > 0 && (
                      <div>
                        <h4 className="font-medium mb-3">Depodan Verilen Malzemeler</h4>
                        <ScrollArea className="h-[200px]">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>Tarih</TableHead>
                                <TableHead>Malzeme</TableHead>
                                <TableHead>Miktar</TableHead>
                                <TableHead>Birim</TableHead>
                                <TableHead>Veren</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {depoMalzemeleri.map(item => (
                                <TableRow key={item.id}>
                                  <TableCell>{formatDate(item.created_at)}</TableCell>
                                  <TableCell className="font-medium">{item.malzeme_adi || item.product_name}</TableCell>
                                  <TableCell>{item.miktar || item.quantity}</TableCell>
                                  <TableCell>{item.birim || item.unit}</TableCell>
                                  <TableCell>{item.veren_ad || '-'}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </ScrollArea>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Detail Modal */}
        <Dialog open={detailModalOpen} onOpenChange={setDetailModalOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Kayıt Detayı</DialogTitle>
            </DialogHeader>
            {selectedItem && (
              <div className="space-y-4">
                {selectedItem.title && (
                  <div><strong>Başlık:</strong> {selectedItem.title}</div>
                )}
                {selectedItem.subject && (
                  <div><strong>Konu:</strong> {selectedItem.subject}</div>
                )}
                {selectedItem.work_type && (
                  <div><strong>İş Türü:</strong> {selectedItem.work_type}</div>
                )}
                {selectedItem.amount && (
                  <div><strong>Miktar:</strong> {Number(selectedItem.amount).toLocaleString('tr-TR')} TL</div>
                )}
                {selectedItem.description && (
                  <div><strong>Açıklama:</strong> {selectedItem.description}</div>
                )}
                {selectedItem.content && (
                  <div><strong>İçerik:</strong> {selectedItem.content}</div>
                )}
                {selectedItem.documents?.length > 0 && (
                  <div>
                    <strong>Belgeler:</strong>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {selectedItem.documents.map((doc, idx) => (
                        <a 
                          key={idx}
                          href={doc.data || doc.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:underline text-sm"
                        >
                          📄 {doc.name || `Belge ${idx + 1}`}
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Sözleşme Ekle Modal - Detail View içinde */}
        <Dialog open={showContractModal} onOpenChange={(open) => { if (!open) { setShowContractModal(false); resetContractForm(); } }}>
          <DialogContent className="max-w-2xl" data-testid="add-contract-modal">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileSignature className="h-5 w-5 text-orange-600" />
                Sözleşme Ekle
                {selectedSubcontractor && (
                  <Badge className="bg-orange-100 text-orange-700 ml-2">
                    {selectedSubcontractor.name || selectedSubcontractor.company_name}
                  </Badge>
                )}
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4 py-4">
              {/* İş Türü */}
              <div className="space-y-2">
                <Label htmlFor="work_type">İş Türü *</Label>
                <Input
                  id="work_type"
                  placeholder="Yapılacak iş türü (örn: İnşaat, Elektrik, Tesisat)"
                  value={newContract.work_type}
                  onChange={(e) => setNewContract(prev => ({ ...prev, work_type: e.target.value }))}
                  data-testid="contract-work-type-input"
                />
              </div>

              {/* Tarihler */}
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="contract_date">Sözleşme Tarihi</Label>
                  <Input
                    id="contract_date"
                    type="date"
                    value={newContract.contract_date}
                    onChange={(e) => setNewContract(prev => ({ ...prev, contract_date: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="start_date">Başlangıç Tarihi</Label>
                  <Input
                    id="start_date"
                    type="date"
                    value={newContract.start_date}
                    onChange={(e) => setNewContract(prev => ({ ...prev, start_date: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="end_date">Bitiş Tarihi</Label>
                  <Input
                    id="end_date"
                    type="date"
                    value={newContract.end_date}
                    onChange={(e) => setNewContract(prev => ({ ...prev, end_date: e.target.value }))}
                  />
                </div>
              </div>

              {/* Tutar */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="contract_amount">Sözleşme Tutarı</Label>
                  <div className="flex gap-2">
                    <Input
                      id="contract_amount"
                      type="number"
                      placeholder="0.00"
                      value={newContract.contract_amount}
                      onChange={(e) => setNewContract(prev => ({ ...prev, contract_amount: e.target.value }))}
                      className="flex-1"
                    />
                    <Select
                      value={newContract.currency}
                      onValueChange={(value) => setNewContract(prev => ({ ...prev, currency: value }))}
                    >
                      <SelectTrigger className="w-24">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="TL">₺ TL</SelectItem>
                        <SelectItem value="USD">$ USD</SelectItem>
                        <SelectItem value="EUR">€ EUR</SelectItem>
                        <SelectItem value="GBP">£ GBP</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Açıklama */}
              <div className="space-y-2">
                <Label htmlFor="contract_desc">Açıklama</Label>
                <Textarea
                  id="contract_desc"
                  placeholder="Sözleşme hakkında açıklama..."
                  value={newContract.description}
                  onChange={(e) => setNewContract(prev => ({ ...prev, description: e.target.value }))}
                  rows={2}
                />
              </div>

              {/* Belgeler */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    Sözleşme Belgeleri
                  </Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => contractFileInputRef.current?.click()}
                    data-testid="upload-contract-doc-btn"
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    Belge Yükle
                  </Button>
                  <input
                    ref={contractFileInputRef}
                    type="file"
                    multiple
                    className="hidden"
                    onChange={handleContractFileUpload}
                  />
                </div>
                
                {newContract.documents.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {newContract.documents.map((doc, index) => (
                      <div key={index} className="flex items-center gap-2 bg-gray-100 px-3 py-2 rounded-lg">
                        <File className="h-4 w-4 text-blue-600" />
                        <span className="text-sm truncate max-w-[150px]">{doc.name || doc.filename}</span>
                        <button
                          type="button"
                          onClick={() => removeContractDocument(index)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <DialogFooter className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => { setShowContractModal(false); resetContractForm(); }}
                disabled={contractLoading}
              >
                İptal
              </Button>
              <Button
                onClick={handleSaveContract}
                disabled={contractLoading}
                className="bg-orange-600 hover:bg-orange-700"
                data-testid="save-contract-btn"
              >
                {contractLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Kaydediliyor...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Kaydet
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // Main list view
  return (
    <div className="space-y-6" data-testid="purchasing-subcontractors">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-orange-100 rounded-lg">
            <HardHat className="h-6 w-6 text-orange-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Taşeronlar</h1>
            <p className="text-sm text-gray-500">Tüm şubelerdeki taşeronları görüntüleyin</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={loadSubcontractors} className="gap-2">
            <RefreshCw className="h-4 w-4" />
            Yenile
          </Button>
          <Button onClick={() => setShowAddModal(true)} className="gap-2 bg-orange-600 hover:bg-orange-700" data-testid="add-subcontractor-btn">
            <Plus className="h-4 w-4" />
            Yeni Taşeron Ekle
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <HardHat className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Toplam Taşeron</p>
                <p className="text-2xl font-bold">{totalSubcontractors}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Users className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Toplam Çalışan</p>
                <p className="text-2xl font-bold">{totalEmployees}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Building2 className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Şube Sayısı</p>
                <p className="text-2xl font-bold">{branchCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Taşeron ara..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="subcontractor-search"
              />
            </div>
            <div className="w-full md:w-64">
              <Select value={selectedBranch} onValueChange={setSelectedBranch}>
                <SelectTrigger data-testid="branch-filter">
                  <Filter className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Şube Filtrele" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tüm Şubeler</SelectItem>
                  {branches.map(branch => (
                    <SelectItem key={branch} value={branch}>{branch}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Subcontractors List */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-orange-600" />
            </div>
          ) : filteredSubcontractors.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <HardHat className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>Taşeron bulunamadı</p>
            </div>
          ) : (
            <ScrollArea className="h-[500px]">
              <Table>
                <TableHeader>
                  <TableRow>
                    {isMuhasebeMode && <TableHead className="w-10"></TableHead>}
                    <TableHead>Taşeron Adı</TableHead>
                    <TableHead>Şube</TableHead>
                    <TableHead>Görev Tanımı</TableHead>
                    <TableHead className="text-center">Çalışan</TableHead>
                    {!isMuhasebeMode && <TableHead>İşlem</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredSubcontractors.map(sub => (
                    <React.Fragment key={sub.id}>
                      <TableRow 
                        className={`cursor-pointer hover:bg-orange-50 ${expandedSubcontractorId === sub.id ? 'bg-orange-50' : ''}`}
                        onClick={() => isMuhasebeMode ? toggleExpandSubcontractor(sub) : openSubcontractorDetail(sub)}
                        data-testid={`subcontractor-row-${sub.id}`}
                      >
                        {isMuhasebeMode && (
                          <TableCell className="w-10">
                            <ChevronDown className={`h-4 w-4 text-gray-500 transition-transform ${expandedSubcontractorId === sub.id ? 'rotate-180' : ''}`} />
                          </TableCell>
                        )}
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <div className="p-1.5 bg-orange-100 rounded">
                              <HardHat className="h-4 w-4 text-orange-600" />
                            </div>
                            <span className="font-medium">{sub.name || sub.company_name || 'İsimsiz'}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="gap-1">
                            <MapPin className="h-3 w-3" />
                            {sub.branch_name || 'Bilinmiyor'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-gray-600 max-w-[200px] truncate">
                          {sub.company_duties || '-'}
                        </TableCell>
                        <TableCell className="text-center">
                          {sub.completion_requests && sub.completion_requests.length > 0 ? (
                            <div className="flex flex-col items-center gap-1">
                              {sub.completion_requests.map((req, i) => (
                                <Badge key={i} variant="outline" className="bg-amber-100 text-amber-700 text-[10px] py-0 text-center leading-tight">
                                  {req.requested_by_name}<br/>görevi bitirdi
                                </Badge>
                              ))}
                            </div>
                          ) : (
                            <Badge className="bg-blue-100 text-blue-700">
                              {sub.employee_count || 0} Çalışan
                            </Badge>
                          )}
                        </TableCell>
                        {!isMuhasebeMode && (
                          <TableCell>
                            <div className="flex items-center gap-2">
                              {(!sub.completion_requests || !sub.completion_requests.some(r => r.requested_by === user?.id)) && (
                                <Button 
                                  size="sm" 
                                  variant="outline" 
                                  onClick={(e) => { e.stopPropagation(); handleFinishSubcontractorTask(sub.id); }}
                                  className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                                  title="Görevini Bitirdi Olarak Bildir"
                                >
                                  <CheckCircle className="h-4 w-4 mr-1" /> Görevi Bitir
                                </Button>
                              )}
                              <Button 
                                size="sm" 
                                variant="outline" 
                                onClick={(e) => { e.stopPropagation(); openSubcontractorDetail(sub); }}
                                data-testid={`view-subcontractor-${sub.id}`}
                              >
                                <Eye className="h-4 w-4" /> Detaylar
                              </Button>
                            </div>
                          </TableCell>
                        )}
                      </TableRow>
                      
                      {/* Expanded Hakediş Row - Muhasebe modunda */}
                      {isMuhasebeMode && expandedSubcontractorId === sub.id && (
                        <TableRow>
                          <TableCell colSpan={5} className="bg-gray-50 p-4">
                            {expandedLoading ? (
                              <div className="flex justify-center py-4">
                                <Loader2 className="h-6 w-6 animate-spin text-orange-600" />
                              </div>
                            ) : (() => {
                              const hakedislerToShow = expandedHakedisData.filter(h => 
                                h.branch_manager_approved && h.management_approved && h.purchasing_approved
                              );
                              
                              return hakedislerToShow.length === 0 ? (
                                <div className="text-center py-4 text-gray-500">
                                  <Receipt className="h-8 w-8 mx-auto mb-2 opacity-50" />
                                  <p className="text-sm">Onaylı hakediş bulunmuyor (3 onay gerekli)</p>
                                </div>
                              ) : (
                                <div className="space-y-2">
                                  <div className="text-sm font-medium text-gray-700 mb-2">Onaylı Hakedişler</div>
                                  <Table>
                                    <TableHeader>
                                      <TableRow className="bg-white">
                                        <TableHead className="text-xs">Tarih</TableHead>
                                        <TableHead className="text-xs">Başlık</TableHead>
                                        <TableHead className="text-xs">Miktar</TableHead>
                                        <TableHead className="text-xs">Onaylar</TableHead>
                                        <TableHead className="text-xs">İşlem</TableHead>
                                      </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                      {hakedislerToShow.map(item => (
                                        <TableRow key={item.id} className="bg-white">
                                          <TableCell className="text-sm">{formatDate(item.hakedis_date)}</TableCell>
                                          <TableCell className="text-sm font-medium">{item.title}</TableCell>
                                          <TableCell className="text-sm font-semibold text-green-600">
                                            {item.amount ? `${Number(item.amount).toLocaleString('tr-TR')} TL` : '-'}
                                          </TableCell>
                                          <TableCell>
                                            <div className="flex gap-1 flex-wrap">
                                              <Badge className="bg-green-100 text-green-700 text-xs">Şube ✓</Badge>
                                              <Badge className="bg-blue-100 text-blue-700 text-xs">Yönetim ✓</Badge>
                                              <Badge className="bg-purple-100 text-purple-700 text-xs">Satın Alma ✓</Badge>
                                            </div>
                                          </TableCell>
                                          <TableCell>
                                            {!item.sent_to_jobs ? (
                                              <Button 
                                                size="sm" 
                                                className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs"
                                                onClick={(e) => { e.stopPropagation(); handleSendToApprovedJobs(item, sub.id); }}
                                              >
                                                İşlere Gönder
                                              </Button>
                                            ) : (
                                              <Badge className="bg-emerald-100 text-emerald-700 text-xs">İşlere Gönderildi</Badge>
                                            )}
                                          </TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                </div>
                              );
                            })()}
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </ScrollArea>
          )}
        </CardContent>
      </Card>

      {/* Yeni Taşeron Ekle Modal */}
      <Dialog open={showAddModal} onOpenChange={(open) => { if (!open) { setShowAddModal(false); resetNewSubcontractorForm(); } }}>
        <DialogContent className="max-w-2xl" data-testid="add-subcontractor-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <HardHat className="h-5 w-5 text-orange-600" />
              Yeni Taşeron Ekle
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Temel Bilgiler */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="sub_name">Taşeron Adı (Cari Hesap) *</Label>
                <Select
                  value={newSubcontractor.cari_hesap_id}
                  onValueChange={(value) => {
                    const selected = cariHesaplar.find(c => c.id === value);
                    setNewSubcontractor(prev => ({ 
                      ...prev, 
                      cari_hesap_id: value,
                      name: selected?.name || ''
                    }));
                  }}
                >
                  <SelectTrigger data-testid="new-sub-name-select">
                    <SelectValue placeholder="Cari hesap seçin" />
                  </SelectTrigger>
                  <SelectContent>
                    {cariHesaplar.map(cari => (
                      <SelectItem key={cari.id} value={cari.id}>
                        {cari.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="sub_branch">Şube *</Label>
                <Select
                  value={newSubcontractor.branch_id}
                  onValueChange={(value) => setNewSubcontractor(prev => ({ ...prev, branch_id: value }))}
                >
                  <SelectTrigger data-testid="new-sub-branch-select">
                    <SelectValue placeholder="Şube seçin" />
                  </SelectTrigger>
                  <SelectContent>
                    {allBranches.map(branch => (
                      <SelectItem key={branch.id} value={branch.id}>
                        {branch.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="sub_duties">Görev Tanımı / Verdiği Hizmet</Label>
              <Textarea
                id="sub_duties"
                placeholder="Taşeronun yapacağı işler veya verdiği hizmet..."
                value={newSubcontractor.provided_service || newSubcontractor.company_duties}
                onChange={(e) => setNewSubcontractor(prev => ({ ...prev, provided_service: e.target.value, company_duties: e.target.value }))}
                rows={2}
                data-testid="new-sub-duties-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>İşe Başlama Tarihi</Label>
                <Input
                  type="date"
                  value={newSubcontractor.start_date}
                  onChange={(e) => setNewSubcontractor(prev => ({ ...prev, start_date: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Bitiş Tarihi</Label>
                <Input
                  type="date"
                  value={newSubcontractor.end_date}
                  onChange={(e) => setNewSubcontractor(prev => ({ ...prev, end_date: e.target.value }))}
                />
              </div>
            </div>

            {/* Custom Fields */}
            <div className="space-y-4 pt-2 border-t">
              <div className="flex items-center justify-between">
                <Label className="text-base font-semibold">Ek Bilgiler (İsteğe Bağlı)</Label>
                <Button 
                  type="button" 
                  variant="outline" 
                  size="sm"
                  onClick={() => {
                    const custom_fields = [...(newSubcontractor.custom_fields || [])];
                    custom_fields.push({ key: '', value: '' });
                    setNewSubcontractor(prev => ({ ...prev, custom_fields }));
                  }}
                  className="gap-1 h-8 text-xs"
                >
                  <Plus className="h-3 w-3" /> Bilgi Ekle
                </Button>
              </div>
              
              {(newSubcontractor.custom_fields || []).map((field, index) => (
                <div key={index} className="flex items-center gap-2">
                  <Input 
                    placeholder="Başlık (Örn: SGK No)"
                    value={field.key}
                    onChange={(e) => {
                      const custom_fields = [...newSubcontractor.custom_fields];
                      custom_fields[index].key = e.target.value;
                      setNewSubcontractor(prev => ({ ...prev, custom_fields }));
                    }}
                    className="flex-1"
                  />
                  <Input 
                    placeholder="Değer (Örn: 12345)"
                    value={field.value}
                    onChange={(e) => {
                      const custom_fields = [...newSubcontractor.custom_fields];
                      custom_fields[index].value = e.target.value;
                      setNewSubcontractor(prev => ({ ...prev, custom_fields }));
                    }}
                    className="flex-1"
                  />
                  <Button 
                    type="button" 
                    variant="ghost" 
                    size="sm" 
                    className="h-10 w-10 p-0 text-red-500"
                    onClick={() => {
                      const custom_fields = [...newSubcontractor.custom_fields];
                      custom_fields.splice(index, 1);
                      setNewSubcontractor(prev => ({ ...prev, custom_fields }));
                    }}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>

            {/* Yetkili Kişi Bilgileri */}
            <div className="border rounded-lg p-4 bg-blue-50/50">
              <div className="flex items-center justify-between mb-3">
                <Label className="flex items-center gap-2 text-blue-700 font-medium">
                  <Users className="h-4 w-4" />
                  Yetkili Kişiler
                </Label>
                <Button 
                  type="button" 
                  variant="outline" 
                  size="sm"
                  onClick={() => {
                    const contacts = [...(newSubcontractor.contacts || [])];
                    contacts.push({ name: '', role: '', phone: '', email: '' });
                    setNewSubcontractor(prev => ({ ...prev, contacts }));
                  }}
                  className="gap-1 h-8 text-xs bg-white text-blue-700 border-blue-200 hover:bg-blue-100"
                >
                  <Plus className="h-3 w-3" /> Yetkili Ekle
                </Button>
              </div>

              {(newSubcontractor.contacts || []).map((contact, index) => (
                <div key={index} className="p-3 bg-white border border-blue-100 rounded-md relative space-y-3 mb-3">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-medium text-blue-600">Yetkili {index + 1}</span>
                    {index > 0 && (
                      <Button 
                        type="button" 
                        variant="ghost" 
                        size="sm" 
                        className="h-6 w-6 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                        onClick={() => {
                          const contacts = [...newSubcontractor.contacts];
                          contacts.splice(index, 1);
                          setNewSubcontractor(prev => ({ ...prev, contacts }));
                        }}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">Ad Soyad</Label>
                      <Input 
                        placeholder="Ad soyad"
                        value={contact.name}
                        onChange={(e) => {
                          const contacts = [...newSubcontractor.contacts];
                          contacts[index].name = e.target.value;
                          setNewSubcontractor(prev => ({ ...prev, contacts }));
                        }}
                        className="h-8 text-xs"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Görev</Label>
                      <Input 
                        placeholder="Görevi"
                        value={contact.role}
                        onChange={(e) => {
                          const contacts = [...newSubcontractor.contacts];
                          contacts[index].role = e.target.value;
                          setNewSubcontractor(prev => ({ ...prev, contacts }));
                        }}
                        className="h-8 text-xs"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Telefon</Label>
                      <Input 
                        placeholder="Telefon"
                        value={contact.phone}
                        onChange={(e) => {
                          const contacts = [...newSubcontractor.contacts];
                          contacts[index].phone = e.target.value;
                          setNewSubcontractor(prev => ({ ...prev, contacts }));
                        }}
                        className="h-8 text-xs"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">E-posta</Label>
                      <Input 
                        type="email"
                        placeholder="E-posta"
                        value={contact.email}
                        onChange={(e) => {
                          const contacts = [...newSubcontractor.contacts];
                          contacts[index].email = e.target.value;
                          setNewSubcontractor(prev => ({ ...prev, contacts }));
                        }}
                        className="h-8 text-xs"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Ek Bilgiler */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="sub_tax">Vergi Numarası</Label>
                <Input
                  id="sub_tax"
                  placeholder="Vergi numarası"
                  value={newSubcontractor.tax_number}
                  onChange={(e) => setNewSubcontractor(prev => ({ ...prev, tax_number: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="sub_address">Adres</Label>
                <Input
                  id="sub_address"
                  placeholder="Adres bilgisi"
                  value={newSubcontractor.address}
                  onChange={(e) => setNewSubcontractor(prev => ({ ...prev, address: e.target.value }))}
                />
              </div>
            </div>
          </div>

          <DialogFooter className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => { setShowAddModal(false); resetNewSubcontractorForm(); }}
              disabled={addLoading}
            >
              İptal
            </Button>
            <Button
              onClick={handleAddSubcontractor}
              disabled={addLoading}
              className="bg-orange-600 hover:bg-orange-700"
              data-testid="save-new-subcontractor-btn"
            >
              {addLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Kaydediliyor...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Kaydet
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PurchasingSubcontractors;
